import { token } from '@atlaskit/tokens';
import { useFlaggingCallback } from '@databases/flags';
import { ScrollPosition } from '@databases/layout/scroll-position';
import { usePrevious } from '@databases/react';
import { DefaultRootState, defaultStoreUtils } from '@databases/redux';
import { selectSelectedEntireEntryIds } from '@databases/redux/cellSelections/selectors';
import { addChangeToDraft } from '@databases/redux/changesets/actions';
import { selectDatabaseDraftByContentId } from '@databases/redux/changesets/selectors';
import { DatabaseFieldType, SelectOption } from '@databases/types/database';
import { DatabaseViewSettings } from '@databases/types/database/view';
import {
  DatabaseChangeType,
  MultiValueChangeType,
} from '@databases/utils/database/changesets';
import { isEmptyValue } from '@databases/utils/database/field-types';
import {
  getConvertedDatabaseValue,
  getCoverField,
  getDatabaseEntryRequired,
  getDatabaseFields,
} from '@databases/utils/database/utils';
import { getGroupField } from '@databases/utils/database/view/view';
import { collabDatabaseBatch } from '@databases/utils/redux/collab/batch';
import { batch } from '@databases/utils/redux/redux';
import { encodeCssUrl } from '@databases/utils/utils';
import { sum } from 'lodash-es';
import svgToMiniDataURI from 'mini-svg-data-uri';
import React, { FunctionComponent, Ref, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

import {
  DraggingIem,
  DragOverItem,
  DragType,
  VerticalPositions,
  useDragItems,
} from '../../../drag-utils';
import {
  getSkeletonRowSvg,
  useAnimateItems,
  useRefMapWithSizes,
} from '../../../lazy-render';
import { useScope } from '../../Scope';
import EntryCardWithMenu from '../entry-card/EntryCardWithMenu';
import { getEntryCardHeightPrediction } from '../entry-card/height';

import BoardLayoutAddEntryButton from './BoardLayoutAddEntryButton';
import BoardLayoutColumnHeader from './BoardLayoutColumnHeader';

const GRID_GAP = 8;
const CONTAINER_PADDING_TOP = 37;

const ADD_ITEM = 'addItem';
const DRAG_ITEM = 'dragItem';
const GAP_TYPE = 'gap';
const ROW_TYPE = 'row';

/**
 * Returns the entry IDs of the entries that should be shown for a specific column in the board layout, in order, for the database draft in
 * the given scope with the given view settings applied.
 */
export function selectBoardLayoutColumnEntries(
  state: DefaultRootState,
  {
    contentId,
    scope,
    viewSettings,
    optionId,
  }: {
    contentId: string;
    scope: string;
    viewSettings: DatabaseViewSettings;
    optionId: string | undefined;
  }
): string[] {
  const databaseDraft = selectDatabaseDraftByContentId(state, {
    contentId,
    scope,
    includeDeleted: true,
  });
  const databaseDraftView = selectDatabaseDraftByContentId(state, {
    contentId,
    scope,
    viewSettings,
    includeDeleted: true,
  });
  const groupField = getGroupField(databaseDraft, viewSettings.layout)!;
  return databaseDraftView.entries.filter((entryId) => {
    const entry = getDatabaseEntryRequired(databaseDraft, entryId);
    const value = getConvertedDatabaseValue(databaseDraft, groupField, entry, state);
    return (
      value.type === DatabaseFieldType.SELECT &&
      (optionId != null
        ? value.value.includes(optionId)
        : isEmptyValue(
            { database: databaseDraftView, field: groupField, entry, value },
            state
          ))
    );
  });
}

const PADDING = 8;

const Container = styled.div`
  position: relative;
  background: ${token('elevation.surface.raised.hovered')};
  border-radius: 3px;
  padding: ${PADDING}px;
  display: flex;
  flex-direction: column;

  > *:first-child {
    margin-bottom: -${GRID_GAP}px;
  }

  > * + * {
    margin-top: ${GRID_GAP}px;
  }
`;

export interface BoardLayoutColumnProps {
  contentId: string;
  isContentEditable: boolean;
  canEditFields: boolean;
  canEditContent: boolean;
  viewSettings: DatabaseViewSettings;
  option: SelectOption | undefined;
  renderSection: ScrollPosition;
  columnWidth: number;
  currentViewId: string;
}

const BoardLayoutColumn: FunctionComponent<BoardLayoutColumnProps> = ({
  contentId,
  isContentEditable,
  canEditFields,
  canEditContent,
  viewSettings,
  option,
  renderSection,
  columnWidth,
  currentViewId,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);

  const scope = useScope();
  const [entryIds, selectedEntryIds, fieldCount, hasCover] =
    defaultStoreUtils.useEqualSelector((state) => {
      const databaseDraft = selectDatabaseDraftByContentId(state, {
        contentId,
        scope,
        includeDeleted: true,
      });
      const databaseDraftView = selectDatabaseDraftByContentId(state, {
        contentId,
        scope,
        viewSettings,
        includeDeleted: false,
      });
      const entryIds = selectBoardLayoutColumnEntries(state, {
        contentId,
        scope,
        viewSettings,
        optionId: option?.id ?? option?.label,
      });
      return [
        entryIds,
        selectSelectedEntireEntryIds(state, {
          contentId,
          scope,
          viewSettings,
        }),
        getDatabaseFields(databaseDraftView).length,
        !!getCoverField(databaseDraft, viewSettings?.layout.coverFieldId),
      ];
    });

  const [dragOver, setDragOver] = useState<DragOverItem>();
  /** The IDs of cards to show. A card can be an entry card or the "add entry" card */
  const cardIds = useMemo(() => {
    const result = [...entryIds];

    if (dragOver) {
      result.splice(dragOver.idx, 0, DRAG_ITEM);
    }

    if (canEditContent) {
      result.push(ADD_ITEM);
    }

    return result;
  }, [entryIds, dragOver, canEditContent]);

  const [draggingItem, setDraggingItem] = useState<DraggingIem>();
  const cardRefMap = useRefMapWithSizes({
    getPositions: ({ sizes }) => {
      const cardPositions: Record<string, VerticalPositions> = {};
      let currentTop = CONTAINER_PADDING_TOP;
      for (const cardId of cardIds) {
        const top = currentTop;
        const height =
          cardId === DRAG_ITEM
            ? dragOver?.height ?? 0
            : cardId === draggingItem?.id
            ? -GRID_GAP
            : sizes[cardId]?.height ??
              getEntryCardHeightPrediction({
                cardWidth: columnWidth,
                fieldCount,
                hasCover,
              });
        cardPositions[cardId] = { top, height };
        currentTop += height + GRID_GAP;
      }

      return cardPositions;
    },
  });

  const store = defaultStoreUtils.useStore();
  const dispatch = defaultStoreUtils.useDispatch();
  const onDrop = useFlaggingCallback(
    // eslint-disable-next-line require-await
    async ({ idx, ids }) => {
      const state = store.getState();
      const databaseDraft = selectDatabaseDraftByContentId(state, {
        contentId,
        scope,
        includeDeleted: true,
      });
      const groupField = getGroupField(databaseDraft, viewSettings.layout);
      const optionAfter = entryIds[idx];

      // eslint-disable-next-line require-await
      batch(async () => {
        collabDatabaseBatch(store, contentId, () => {
          dispatch(
            addChangeToDraft(contentId, scope, {
              type: DatabaseChangeType.REORDER_ENTRIES,
              entryIds: ids,
              newIndex: optionAfter
                ? databaseDraft.entries.indexOf(optionAfter)
                : databaseDraft.entries.length,
            })
          );
          for (const entryId of ids) {
            dispatch(
              addChangeToDraft(contentId, scope, {
                type: DatabaseChangeType.SET_VALUE,
                entryId,
                fieldId: groupField!.id,
                value: {
                  type: DatabaseFieldType.SELECT,
                  value: option ? [option.id ?? option.label] : [],
                },
                change: {
                  type: DatabaseFieldType.SELECT,
                  delta: {
                    type: MultiValueChangeType.SET_TO,
                    value: option ? [option.id ?? option.label] : [],
                  },
                },
              })
            );
          }
        });
      });
    },
    [contentId, dispatch, entryIds, option, scope, store, viewSettings.layout]
  );

  const {
    handleDragLeave,
    handleDragEnter,
    handleDragEnd,
    handleDrag,
    handleDrop,
    handleDragOver,
  } = useDragItems({
    dragDirection: 'vertical',
    ids: entryIds,
    rowOrColIds: cardIds,
    contentId,
    positions: cardRefMap.positions,
    dragType: DragType.ENTRY_CARD,
    onDrop,
    onDragOver: setDragOver,
    onSetDraggingItem: setDraggingItem,
    topOrLeftPadding: containerRef.current?.getBoundingClientRect().top ?? 0,
  });

  const prevIsDragOver = usePrevious(!!dragOver);
  const renderCardIds = useAnimateItems({
    itemIds: cardIds,
    itemRefMap: cardRefMap,
    renderSection,
    transitionTime: dragOver || prevIsDragOver ? '0.2s' : undefined,
  });

  const isSelectedCardDragging = useFlaggingCallback(
    (cardId: string) => selectedEntryIds.includes(cardId) && !!draggingItem,
    [draggingItem, selectedEntryIds]
  );

  /**
   * The rows that should actually be rendered. These include visible cards, cards that need to be animated, a card
   * that is currently being dragged, and gap rows.
   */
  // Cannot memoize because heights might change
  const renderRows = (() => {
    let gapHeight: number[] = [];
    const rows: Array<
      | { type: typeof GAP_TYPE; height: number }
      | { type: typeof ROW_TYPE; cardId: string }
    > = [];
    for (const cardId of cardIds) {
      if (renderCardIds.includes(cardId) || cardId === draggingItem?.id) {
        if (gapHeight.length > 0) {
          rows.push({
            type: GAP_TYPE,
            height: sum(gapHeight) + GRID_GAP * (gapHeight.length - 1),
          });
          gapHeight = [];
        }

        rows.push({ type: ROW_TYPE, cardId });
      } else {
        gapHeight.push(cardRefMap.positions[cardId].height);
      }
    }
    if (gapHeight.length > 0) {
      rows.push({
        type: GAP_TYPE,
        height: sum(gapHeight) + GRID_GAP * (gapHeight.length - 1),
      });
    }
    return rows;
  })();

  const skeletonSvg = useMemo(
    () => svgToMiniDataURI(getSkeletonRowSvg([columnWidth - PADDING * 2])),
    [columnWidth]
  );

  return (
    <Container
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      ref={containerRef}
    >
      <BoardLayoutColumnHeader
        contentId={contentId}
        canEditFields={canEditFields}
        option={option}
        viewSettings={viewSettings}
      />

      {renderRows.map((row, i) =>
        row.type === GAP_TYPE ? (
          <div
            key={`gap-${i}`}
            style={{
              height: row.height,
              background: `url(${encodeCssUrl(skeletonSvg)}) repeat-y`,
            }}
          />
        ) : row.cardId === ADD_ITEM ? undefined : row.cardId === DRAG_ITEM ? ( // Will be rendered below
          <div
            key="dragItem"
            ref={
              dragOver ? (cardRefMap.refs[dragOver.id] as Ref<HTMLDivElement>) : undefined
            }
            style={{ height: dragOver?.height }}
          />
        ) : (
          <EntryCardWithMenu
            key={row.cardId}
            contentId={contentId}
            entryId={row.cardId}
            viewSettings={viewSettings}
            isReadonly={!isContentEditable}
            isDraggable={canEditContent}
            isDragging={draggingItem?.id === row.cardId}
            onDrag={handleDrag}
            onDragEnd={handleDragEnd}
            currentViewId={currentViewId}
            ref={{
              current: {
                entryCardRef: isSelectedCardDragging(row.cardId)
                  ? undefined
                  : cardRefMap.refs[row.cardId],
                boardLayoutColumnRef: containerRef,
              },
            }}
            style={
              isSelectedCardDragging(row.cardId)
                ? { height: 0, overflow: 'auto', marginTop: 0 }
                : undefined
            }
          />
        )
      )}

      {renderRows.some((row) => row.type === ROW_TYPE && row.cardId === ADD_ITEM) && (
        <BoardLayoutAddEntryButton
          viewSettings={viewSettings}
          option={option}
          ref={cardRefMap.refs[ADD_ITEM]}
        />
      )}
    </Container>
  );
};

export default BoardLayoutColumn;
